import React, { useState } from "react";

import { fetchPreviewAsset, fetchPreviewHtml } from "../../api/minisite";
import {
  NOMS_BD,
  libelleBd,
  nomsPhotos,
  placeholderManquant,
  residusBd,
  substituerAssets,
} from "./apercu";

const LARGEURS = { ordinateur: "100%", telephone: 390 };

// Ouvre le mini-site tel qu'il serait publié aujourd'hui : versions BD
// validées, découpe mobile, planche complète si elle est validée. Rien n'est
// publié, rien n'est modifié. Hors de `computeState` de PublishButton, à
// dessein : un brouillon incomplet mérite aussi un aperçu.
export default function ApercuButton({ clientId, draft }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [apercu, setApercu] = useState(null); // { html, manquants }
  const [largeur, setLargeur] = useState("ordinateur");

  const fermer = () => setApercu(null);

  const ouvrir = async () => {
    setBusy(true);
    setError(null);
    try {
      const html = await fetchPreviewHtml(clientId);
      const noms = NOMS_BD.concat(nomsPhotos(draft));
      const images = await Promise.all(noms.map((n) => fetchPreviewAsset(clientId, n)));
      const urls = {};
      const manquants = [];
      noms.forEach((nom, i) => {
        if (images[i]) {
          urls[nom] = images[i];
        } else if (NOMS_BD.includes(nom)) {
          urls[nom] = placeholderManquant(libelleBd(nom));
          manquants.push(libelleBd(nom));
        } else {
          urls[nom] = placeholderManquant("photo", `${nom} introuvable`);
          manquants.push(`photo ${nom}`);
        }
      });
      const rendu = substituerAssets(html, urls);
      const residus = residusBd(rendu);
      if (residus.length) {
        throw new Error(`Aperçu incomplet : ${residus.join(", ")} non résolus`);
      }
      // La planche complète n'est référencée que si elle est validée : ne pas
      // la compter absente quand la page ne la demande pas.
      const utiles = manquants.filter(
        (m) => m !== "planche complète" || html.includes("bd_complete.jpg")
      );
      setApercu({ html: rendu, manquants: utiles });
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <button
          type="button"
          disabled={busy}
          onClick={ouvrir}
          style={{
            background: "#fff",
            color: "#111",
            border: "1px solid #111",
            padding: "10px 20px",
            borderRadius: 6,
            cursor: busy ? "wait" : "pointer",
            fontWeight: 600,
          }}
        >
          {busy ? "Aperçu en cours..." : "Aperçu"}
        </button>
        {error && <span style={{ color: "red", fontSize: 12 }}>{error}</span>}
      </div>

      {apercu && (
        <div
          role="dialog"
          aria-label="Aperçu du mini-site"
          style={{
            position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.7)",
            display: "flex", flexDirection: "column",
          }}
        >
          <div style={{
            display: "flex", alignItems: "center", gap: 12, padding: "10px 16px",
            background: "#fff", flexWrap: "wrap",
          }}>
            <strong>Aperçu</strong>
            <span style={{ fontSize: 12, color: "#666" }}>
              Le site tel qu'il serait publié maintenant. Rien n'est mis en ligne.
            </span>
            {Object.keys(LARGEURS).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setLargeur(k)}
                style={{
                  background: largeur === k ? "#000" : "transparent",
                  color: largeur === k ? "#fff" : "#000",
                  border: "1px solid #000", padding: "4px 10px", borderRadius: 4, cursor: "pointer",
                }}
              >
                {k === "ordinateur" ? "Ordinateur" : "Téléphone"}
              </button>
            ))}
            {apercu.manquants.length > 0 && (
              <span style={{ fontSize: 12, color: "#b45309" }}>
                Absent de l'aperçu (non validé ou manquant) : {apercu.manquants.join(", ")}
              </span>
            )}
            <button type="button" onClick={fermer} style={{ marginLeft: "auto", padding: "6px 14px", cursor: "pointer" }}>
              Fermer
            </button>
          </div>
          <div style={{ flex: 1, display: "flex", justifyContent: "center", overflow: "hidden", padding: 12 }}>
            <iframe
              title="Aperçu du mini-site"
              srcDoc={apercu.html}
              // `allow-scripts` sans `allow-same-origin` : les animations et le
              // zoom du mini-site fonctionnent, mais la page rendue tourne dans
              // une origine opaque — elle ne peut ni lire le jeton de session
              // ni toucher à l'application autour.
              sandbox="allow-scripts"
              style={{ width: LARGEURS[largeur], maxWidth: "100%", height: "100%", border: 0, background: "#fff" }}
            />
          </div>
        </div>
      )}
    </>
  );
}
